import React from 'react';
import { connect } from 'react-redux';
import { genURL } from './ActionUtils';
import Link from './Link';
import GuidePage from './GuidePage';
import './FAQ.css';

const questions = {
  "ethereum": {
    id: "ethereum",
    title: "What is Ethereum?",
    answer: <>
        <p><Link noTarget={true} rel="noopener noreferrer" href={"https://en.wikipedia.org/wiki/Ethereum"}>Ethereum</Link> is a cryptocurrency (similar
          to <Link noTarget={true} rel="noopener noreferrer" href={"https://en.wikipedia.org/wiki/Bitcoin"}>Bitcoin</Link>)
          and an open financial platform which is available to anyone in the world with an Internet connection.</p>
    </>
  },
  "whyethereum": {
    id: "whyethereum",
    title: "Why does Atstake use Ethereum?",
    answer: <>
        <p>Ethereum enables money to be held in a mechanism
          called <Link noTarget={true} rel="noopener noreferrer" href={"https://coincenter.org/entry/what-is-multi-sig-and-what-can-it-do"}>two-of-three multisig</Link> such
          that no single individual has full control over it, which reduces your risk
          when contracting with others.
          Before public blockchains like Bitcoin or Ethereum this was not possible via the Internet.
          Making this feature easier to use was our main motivation in creating Atstake.</p>
        <p>Ethereum also allows users to enforce contracts directly between each other without needing to trust any
          specific intermediary (including Atstake). This allows greater control over how contracts are enforced than could otherwise be achieved.</p>
    </>
  },
  "outcome": {
    id: "outcome",
    title: "Who decides the outcome of a contract?",
    answer: <>
        <p>Each contract on Atstake specifies an arbitrator who is responsible for settling any disagreements between the parties.
          The arbitrator can be any willing user of Atstake, as long as everyone agrees. The arbitrator is never called on to
          settle a contract (and is never paid) unless the parties can't agree. The arbitrator can never send contract funds to themselves.</p>
    </>
  },
  "trust": {
    id: "trust",
    title: "Why should I trust the arbitrator to behave honestly?",
    answer: <>
        <p>We've carefully designed Atstake so that all participants have incentives to report
          contract outcomes honestly. <Link href={genURL("/incentives")} noTarget={true} rel="noopener noreferrer">Learn more ></Link></p>
    </>
  },
  "arbitration-fees": {
    id: "arbitration-fees",
    title: "Do I need to pay arbitration fees to use Atstake?",
    answer: <>
        <p>You'll never have to pay an arbitration fee
          unless you disagree with both the arbitrator and your counterparty about the outcome of the contract.
          We've designed a <Link href={genURL("/incentives")} noTarget={true} rel="noopener noreferrer">system of incentives</Link> that makes arbitration rare.
          A reliable strategy for never paying an arbitration fee is to
          only participate in contracts with clear outcome criteria, and to ensure that at least your counterparty or the arbiter
          cares enough about their reputation to not want to publicly lie about the contract outcome.</p>
    </>
  },
  "find-arbitrator": {
    id: "find-arbitrator",
    title: "How can I find an arbitrator for my contract?",
    answer: <>
        <p>Atstake will suggest a default arbitrator for your contracts. If you'd like to choose your
          own, our <Link href={genURL("/people")} noTarget={true} rel="noopener noreferrer">People</Link> page lists arbitrators for you to choose from.
          We track their reputations
          and highlight their fees so you can make an informed choice. If there's anyone who both you and your counterparty know and trust, they
          might be a good choice. If they don't already have an Atstake account they need only an Ethereum address to sign up.</p>
    </>
  },
  "counterparty": {
    id: "counterparty",
    title: "How do I find someone to participate in a contract or transaction with me?",
    answer: <>
        <p>Bring someone you know or have found elsewhere.
          Use 'Create an agreement' on the <Link href={genURL("/")} noTarget={true}>homepage</Link> or
          'New contract' on your <Link href={genURL("/contracts")} noTarget={true}>Contracts</Link> page
          to propose terms directly to them. They do not need to have signed up before you send a proposal.</p>
    </>
  },
  "collusion": {
    id: "collusion",
    title: "Can't my counterparty and the arbitrator collude to steal from me?",
    answer: <>
        <p>This is unlikely if either your counterparty or the arbitrator
          have a reputation that they care about more than the money you're staking.
          This is because our use of Ethereum makes it easy to prove this type of dishonesty.
          For more details read about
          Atstake's <Link href={genURL("/incentives")} noTarget={true} rel="noopener noreferrer">incentives</Link>.</p>
    </>
  },
  "marketplace": {
    id: "marketplace",
    title: "Is Atstake a marketplace?",
    answer: <>
        <p>No. Atstake is a tool for making agreements directly with another person, not for browsing goods or services.
          You choose the other party, the terms, and how any funds will be distributed.</p>
        <p>Read about <Link href={genURL("/incentives")} noTarget={true}>escrow and incentives</Link> before
          committing funds. Ethereum network fees and any arbitration fees specified in your agreement may apply.</p>
    </>
  },
  "getwallet": {
    id: "getwallet",
    title: "How do I get an Ethereum address?",
    answer: <>
        <p>To use Atstake on mobile, download an Ethereum wallet app
          such as <Link noTarget={true} rel="noopener noreferrer" href="https://wallet.coinbase.com">Coinbase Wallet</Link>.
          Then follow the intsructions within the app for creating an address.</p>
        <p>On a desktop
          install <Link noTarget={true} rel="noopener noreferrer" href={"https://metamask.io/"}>Metamask</Link>,
          a browser extension available for the Chrome, Firefox, Brave, and Opera web browsers.
          Follow the instructions for creating an address.
          You'll need to have your Metamask account unlocked to use most of Atstake's functionality.</p>
    </>
  },
  "get-ether": {
    id: "get-ether",
    title: "How do I get some Ether (ETH) so that I can use it in a contract?",
    answer: <>
        <p>The easiest way is to buy it from an exchange using your local currency.
          One user-friendly option is <Link noTarget={true} rel="noopener noreferrer" href={"https://coinbase.com"}>Coinbase</Link>.
          You'll need to send the ETH from the exchange to the Ethereum address you created with Metamask or your mobile
          Ethereum wallet.</p>
    </>
  },
  "test-mode": {
    id: "test-mode",
    title: "Can I try out Atstake without using real money?",
    answer: <>
        <p>Yes. Use your wallet to switch to the Rinkeby Test Network and you'll automatically enter test mode.
          Switch back to the Main Ethereum Network to disable test mode.
          Use <Link href={genURL("/faucet")} noTarget={true} rel="noopener noreferrer">our faucet</Link> to get free test mode Ether and tokens.</p>
        <p>Note that not all Ethereum wallets support Rinkeby.
          If yours doesn't, try one of our <Link href={genURL("/faq#getwallet")} noTarget={true} rel="noopener noreferrer">recommended wallets</Link>.</p>
    </>
  },
  "tokens": {
    id: "tokens",
    title: "Ether is too volatile. Can I use Atstake with a more stable currency?",
    answer: <>
        <p>Yes. We support staking the ERC-20
          tokens  <Link noTarget={true} rel="noopener noreferrer" href={"https://www.coinbase.com/usdc"}>USDC</Link> and
          <Link noTarget={true} rel="noopener noreferrer" href={"https://makerdao.com/en/dai/"}> DAI</Link>, which are intended to track the value
          of the US dollar. If you want to use an ERC-20 token but don't see it on our site,
          email us at support@atstake.net and ask us to add it.</p>
    </>
  },
  "first-deposit": {
    id: "first-deposit",
    title: "Will my funds get stuck if I deposit first but my counterparty refuses to deposit what they owe?",
    answer: <>
        <p>No. Until all required deposits have been made the first person to deposit has the option to withdraw their funds early and cancel the contract.</p>
    </>
  },
  "unconfirmed": {
    id: "unconfirmed",
    title: "What should I do if my transaction doesn't confirm after a very long time?",
    answer: <>
        <p>Usually this is caused by the transaction being sent with too low of a fee. Some wallets unfortunately aren't very good at fee estimation,
          requiting this situation to occassionally be fixed manually.</p>
        <p>If you look at your transaction within your Ethereum wallet there should either
          be a way to cancel it (so you can send another) or speed it up by increasing the transaction fee.
          You can monitor the state of your transactions
          using <Link noTarget={true} rel="noopener noreferrer" href={"https://etherscan.io/"}>Etherscan</Link>.</p>
        <p>If you sent a deposit transaction containing money which was confirmed by the Ethereum network but wasn't detected by Atstake,
          email us at support@atstake.net. You can also access the contract directly on
          our <Link href={genURL("/rawcontracts")} noTarget={true} rel="noopener noreferrer">raw contracts</Link> page.</p>
    </>
  },
  "contract-code": {
    id: "contract-code",
    title: "How do I know that the Ethereum code which controls all contracts and holds all funds is trustworthy?",
    answer: <>
        <p>All of our contract code is available at
          our <Link noTarget={true} rel="noopener noreferrer" href={"https://github.com/atstake/atstake"}> github repository</Link>.
          The repository also contains instructions on how to verify that our deployed code matches this code,
          as well as evidence regarding the security of our code.</p>
    </>
  },
  "identity": {
    id: "identity",
    title: "How do I know whether an Atstake user is really the Twitter/Facebook/Reddit user who they claim to be?",
    answer: <>
        <p>When an Atstake user associates one of their external accounts to an
          Atstake account, we require that they publicly post on this account claiming control of their Ethereum address.
          These posts are available for you to review on any user's Atstake page.</p>
    </>
  },
  "social-accounts": {
    id: "social-accounts",
    title: "Do I have to associate my Atstake account with my social media accounts?",
    answer: <>
        <p>You don't have to link your Atstake account to external accounts (Twitter, Facebook, etc.), but by doing so you show
          others that you're willing to stake your
          reputation on your contracts. This makes other people more likely to enter into contracts with you.
          This works better the more strongly your external accounts are associated with an identity that you care about.</p>
    </>
  },
  "hidden-contracts": {
    id: "hidden-contracts",
    title: "What does clicking the 'x' on a contract when viewing my list of contracts do?",
    answer: <>
        <p>This will move the contract into your 'hidden contracts', which you can
          access with the dropdown above your contracts list. The contract won't move immediately when you click it, in case clicking
          was an accident and you want to 'unhide' it. Instead, the contract will be moved the next time you load your contracts list
          or refresh the page.</p>
    </>
  },
  "unavailable": {
    id: "unavailable",
    title: "What happens to my contracts if Atstake becomes unavailable?",
    answer: <>
        <p>Because Atstake is just an interface to Ethereum contracts, you're
          still be able to interact with your contracts no matter what happens to Atstake.
          We've written <Link noTarget={true} rel="noopener noreferrer"
          href={"https://github.com/atstake/atstake/blob/master/AgreementManager_Manual_Use.md"}>instructions</Link> for
          how to do this.
          You may want to save your own copy of these instructions in case they become unavailable in the future.</p>
        <p>You can get some practice interacting directly with our contracts on
          our <Link href={genURL("/rawcontracts")} noTarget={true} rel="noopener noreferrer">raw contracts</Link> page.</p>
    </>
  },
  "private-terms": {
    id: "private-terms",
    title: "How can I make it impossible for Atstake to see the text of my contract?",
    answer: <>
        <p>Use a <Link noTarget={true} rel="noopener noreferrer" href={"https://passwordsgenerator.net/sha256-hash-generator/"}>hash function</Link> to
          hash the contract text, then submit something like the following to Atstake:
          "Terms are specified by text corresponding to the hash 7ae9027af651d45f2d28be80d0572a425f1c24d09f96dae0472de4c87c54b468."</p>
        <p>When using this technique you'll need to communicate the contract text to your counterparty outside of Atstake.
          This method also allows you to hide the contract details from the arbitrator. only revealing them if there's a dispute.</p>
    </>
  },
  "contact": {
    id: "contact",
    title: "What should I do if I still have a question?",
    answer: <>
        <p>Contact us at support@atstake.net.</p>
    </>
  }
};

const sections = [
  {
    id: 'using-atstake',
    label: 'Using Atstake',
    description: 'Agreement creation and contract management.',
    questions: ['marketplace', 'counterparty', 'ethereum', 'whyethereum', 'hidden-contracts', 'contact']
  },
  {
    id: 'wallets',
    label: 'Wallets & transactions',
    description: 'Addresses, deposits, network fees, and transaction troubleshooting.',
    questions: ['getwallet', 'get-ether', 'test-mode', 'tokens', 'first-deposit', 'unconfirmed']
  },
  {
    id: 'arbitration',
    label: 'Arbitration & trust',
    description: 'Choosing an arbitrator and understanding disagreements.',
    questions: ['outcome', 'trust', 'arbitration-fees', 'find-arbitrator', 'collusion']
  },
  {
    id: 'privacy',
    label: 'Privacy & access',
    description: 'Identity, contract details, and access beyond the website.',
    questions: ['contract-code', 'identity', 'social-accounts', 'unavailable', 'private-terms']
  }
];

function FAQ({ hash }) {
  return (
    <GuidePage page="/faq" eyebrow="Frequently asked questions"
      title="Frequently asked questions"
      sections={sections} hash={hash}>
      <div className="agreementHomeInner guideBody">
        {sections.map((section, index) => (
          <section className="faqTopic" id={section.id} key={section.id} aria-labelledby={section.id + '-title'}>
            <div className="faqTopicHeading">
              <div className="agreementEyebrow">Topic {String(index + 1).padStart(2, '0')}</div>
              <h2 id={section.id + '-title'}>{section.label}</h2>
              <p>{section.description}</p>
            </div>
            <div>
              {section.questions.map(questionId => {
                const question = questions[questionId];
                return (
                  <details className="faqItem" id={question.id} key={question.id} open={hash === '#' + question.id}>
                    <summary><h3>{question.title}</h3><span className="faqToggle" aria-hidden="true" /></summary>
                    <div className="faqAnswer guideCopy">{question.answer}</div>
                  </details>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </GuidePage>
  );
}

export default connect(state => ({ hash: state.menu.hash }))(FAQ);
